import { useEffect, useRef, useState, useCallback } from 'react';
import { useAppStore } from '@/stores/appStore';

const IDLE_TIMEOUT = 90_000; // 90 seconds

export function useKioskMode() {
  const kioskMode = useAppStore((s) => s.settings.kioskMode);
  const reset = useAppStore((s) => s.reset);
  const [isIdle, setIsIdle] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetIdleTimer = useCallback(() => {
    if (!kioskMode) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setIsIdle(false);

    timeoutRef.current = setTimeout(() => {
      setIsIdle(true);
      reset();
    }, IDLE_TIMEOUT);
  }, [kioskMode, reset]);

  const resetIdle = useCallback(() => {
    setIsIdle(false);
    resetIdleTimer();
  }, [resetIdleTimer]);

  useEffect(() => {
    if (!kioskMode) {
      setIsIdle(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'wheel'];

    const handleActivity = () => {
      resetIdleTimer();
    };

    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Start the timer
    resetIdleTimer();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [kioskMode, resetIdleTimer]);

  return { isIdle, resetIdle };
}
